import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { BaseMap } from 'src/app/shared/api/google-maps/base-map';
import { DarkOverlayMapType } from 'src/app/shared/api/google-maps/dark-overlay-map-type';
import { GeoLocationService } from 'src/app/shared/services/geo-location.service';
import { MapsLoaderService } from 'src/app/shared/services/maps-loader.service';
import { RegisteredSignal } from 'src/app/web-api-client';
import { AddSignalRegistrationComponent } from '../../modals/add-signal-registration/add-signalRegistration.component';

@Component({
  selector: 'app-signals-map-view',
  templateUrl: './signals-map-view.component.html',
  styleUrls: ['./signals-map-view.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush
})
export class SignalsMapViewComponent extends BaseMap implements OnInit, OnChanges {
  @ViewChild('map', { static: true }) mapElement: ElementRef<HTMLElement>;
  @Input() signalsList: RegisteredSignal[] = [];
  @Input() focusView: boolean = true;
  @Input() navigationBar: boolean = true;

  private signalMarkersList: google.maps.marker.AdvancedMarkerElement[] = [];
  private infowindow: google.maps.InfoWindow;
  private selectedRegisteredSignal: RegisteredSignal;

  public mapLoaded = false;
  public componentInitialized = false;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly mapsLoaderService: MapsLoaderService,
    protected readonly geoLocationService: GeoLocationService,
    public readonly modalService: NgbModal,
    private zone: NgZone,
  ) {
    super(mapsLoaderService, geoLocationService);
  }

  ngOnInit(): void {
    this.initMap();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(!this.componentInitialized) {
      return;
    }

    if(changes.signalsList && !changes.signalsList.firstChange) {
      this.signalMarkersList.forEach(m => m.map = null);
      this.signalMarkersList = [];

      this.initMarkers();
    }
  }

  public isGridRowSelected(registeredSignal: RegisteredSignal): boolean {
    return this.selectedRegisteredSignal == registeredSignal;
  }

  public onGridRowSelected(signal: RegisteredSignal) {
    this.selectedRegisteredSignal = signal;

    const marker = this.signalMarkersList.find(m => m.position.lat === +signal.latitude && m.position.lng === +signal.longitude);
    if(marker) {
      this.map.panTo(marker.position);
      this.map.setZoom(20);
    }
  }

  protected initMap() {
    super.initMap(this.mapElement, null);

    if(this.focusView) {
      this.map.overlayMapTypes.insertAt(0, new DarkOverlayMapType(new google.maps.Size(256, 256)));
    }

    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      this.mapLoaded = true;
      this.changeDetectorRef.detectChanges();

      this.onMapLoaded();
      this.componentInitialized = true
    });
  }

  private onMapLoaded() {
    this.initMarkers();
  }

  private initMarkers() {
    this.signalsList.forEach(s => {
      const marker = this.createMarker(s);
      this.signalMarkersList.push(marker);
    });

    this.fitMapZoom();
  }

  private createMarker(signal: RegisteredSignal, showOnMap: boolean = true) {
    const incidentMarker = new google.maps.marker.AdvancedMarkerElement({
      map: showOnMap ? this.map : null,
      position: {
        lat: +signal.latitude,
        lng: +signal.longitude
      },
      gmpDraggable: false,
      //content: markerWrapper
    });

    const contentString = `
      <div id="content">
        <h2 id="firstHeading" class="firstHeading" style="font-weight:bold;">${signal.title}</h2>
        <div id="bodyContent">
          <p>${signal.description.replace(/\n/g, '<br>')}</p>
          <button id="viewSignalBtn" class="btn btn-primary">Детайли</button>
        </div>
      </div>`;

    incidentMarker.addListener('click', () => {
      if (!this.infowindow) {
        this.infowindow = new google.maps.InfoWindow({
          maxWidth: 300
        });
      }

      this.infowindow.setContent(contentString);
      this.infowindow.open({
        anchor: incidentMarker,
        map: this.map,
        shouldFocus: false,
      });

      // Listen for button click in the InfoWindow
      google.maps.event.addListenerOnce(this.infowindow, 'domready', () => {
        document.getElementById('viewSignalBtn').addEventListener('click', () => {
          this.zone.run(() => {
            this.openSignal(signal);
          });
        });
      });
    });

    return incidentMarker;
  }


  private createMarker_arch(signal: RegisteredSignal, showOnMap: boolean = true) {
    const incidentMarker = new google.maps.marker.AdvancedMarkerElement({
      map: showOnMap ? this.map : null,
      position: {
        lat: +signal.latitude,
        lng: +signal.longitude
      },
      gmpDraggable: false,
      //content: markerWrapper
    });
    incidentMarker.addListener('click', (ev) => {
      if (!this.infowindow) {
        this.infowindow = new google.maps.InfoWindow({
          content: signal.description,
          maxWidth: 300
        });
      }

      this.infowindow.open({
        anchor: incidentMarker,
        map: this.map,
      });

      this.updateInfoWindow(signal.title);
    });

    return incidentMarker;
  }

  private fitMapZoom() {
    const latLngBounds = this.getMarkersLatLngBounds();
    this.map.fitBounds(latLngBounds);
  }

  private getMarkersLatLngBounds(): google.maps.LatLngBounds {
    const latLngBounds = new google.maps.LatLngBounds();
    this.signalMarkersList.forEach(m => {
      latLngBounds.extend(m.position);
    });

    return latLngBounds;
  }

  private updateInfoWindow(signalTitle) {
    if(this.infowindow) {
      this.infowindow.setContent(signalTitle);
    }
  }

  private openSignal(registeredSignal:RegisteredSignal) {
    const modalOptions: NgbModalOptions = {
      size: 'xl',
      centered: true
    };

    const modalRef = this.modalService.open(AddSignalRegistrationComponent, modalOptions);
    modalRef.closed.subscribe(() => {
      console.log('closed');
    });

    const componentInstance = <AddSignalRegistrationComponent>modalRef.componentInstance;
    componentInstance.registeredSignal = registeredSignal;
  }
}
